import {Box, Button, Heading, Text, VStack, Container} from "@chakra-ui/react";
import {useColorModeValue} from "./ui/color-mode";
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

interface FallbackProps {
    error: Error;
    resetError: () => void;
}

const ErrorFallback = ({ error, resetError }: FallbackProps) => {
    const { t } = useTranslation();
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBgColor = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const errorBg = useColorModeValue('red.50', 'red.900');

    return (
        <Box minH="100vh" bg={bgColor} py={20}>
            <Container maxW="lg">
                <VStack
                    gap={6}
                    bg={cardBgColor}
                    p={8}
                    borderRadius="lg"
                    shadow="lg"
                    textAlign="center"
                >
                    <Heading size="lg" color="red.500">
                        {t('error.title', 'Something went wrong')}
                    </Heading>
                    <Text color={textColor}>
                        {t('error.description', 'An unexpected error occurred. Our team has been notified.')}
                    </Text>
                    {process.env.NODE_ENV === 'development' && (
                        <Box
                            p={4}
                            bg={errorBg}
                            borderRadius="md"
                            w="full"
                            textAlign="left"
                            fontSize="sm"
                            fontFamily="mono"
                        >
                            <Text fontWeight="bold" color="red.700">
                                {error.name}: {error.message}
                            </Text>
                            <Text color="red.600" whiteSpace="pre-wrap" mt={2}>
                                {error.stack}
                            </Text>
                        </Box>
                    )}
                    <Button colorPalette="teal" onClick={resetError}>
                        {t('error.tryAgain', 'Try Again')}
                    </Button>
                    <Button variant="ghost" onClick={() => window.location.href = '/'}>
                        {t('error.goHome', 'Go to Home')}
                    </Button>
                </VStack>
            </Container>
        </Box>
    );
};

interface SentryErrorBoundaryProps {
    children: React.ReactNode;
}

export const SentryErrorBoundary = ({ children }: SentryErrorBoundaryProps) => {
    return (
        <Sentry.ErrorBoundary
            fallback={({ error, resetError }) => (
                <ErrorFallback error={error as Error} resetError={resetError} />
            )}
            onError={(error, componentStack) => {
                Sentry.captureException(error, {
                    contexts: { react: { componentStack } },
                });
            }}
            showDialog={!import.meta.env.DEV}
        >
            {children}
        </Sentry.ErrorBoundary>
    );
};
